import { type JSX, useMemo, useState } from "react";

import { type Catalog, type Operation, canRun } from "../api/catalog";

interface OperationNavProps {
  catalog: Catalog;
  selected: string | null;
  onSelect: (operationId: string) => void;
  /** Roles the chosen identity's token carries — for marking what it will be refused, not for
   * hiding it. Empty is a real answer (a token with no roles reaches almost everything). */
  roles: string[];
}

/**
 * Navigation over the whole API surface, grouped by what a tester is trying to do rather than by
 * URL. Every endpoint the deployment exposes is reachable from here — that is the point.
 *
 * Nothing is hidden by the chosen identity. This list used to drop the operations the identity
 * would be refused, which quietly removed the thing a bench is for: you cannot check that the
 * service refuses an agent token on a lifecycle event if the bench will not let you try. What the
 * identity cannot do is marked instead, and the panel spells out the expected refusal.
 *
 * Only API operations. Reporting and repository setup used to sit in this list too, which made
 * the two things that are not calls look like two more of them; they are top-level tabs now.
 */
/**
 * Operations that are a reporting view rather than a call to make by hand.
 *
 * Token usage is an aggregation -- a required `group_by` and a set of filters -- and Overview
 * renders it as a chart with drill-down. Listing it here as well offered the same endpoint twice
 * and the version in this list was the poorer of the two. The catalogue still holds it, so
 * coverage checks are unaffected; it is only absent from this navigation.
 */
const REPORTING_ONLY = new Set(["get_llm_usage_v1_usage_llm_get"]);

export function OperationNav({
  catalog,
  selected,
  onSelect,
  roles,
}: OperationNavProps): JSX.Element {
  const [filter, setFilter] = useState("");

  const reaches = useMemo(() => (op: Operation) => canRun(op, roles), [roles]);

  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matches = (op: Operation): boolean =>
      needle === "" ||
      op.task_label.toLowerCase().includes(needle) ||
      op.path.toLowerCase().includes(needle) ||
      (op.summary ?? "").toLowerCase().includes(needle);

    return catalog.groups
      .map((group) => ({
        group,
        operations: catalog.operations.filter(
          (op) => op.group === group && matches(op) && !REPORTING_ONLY.has(op.operation_id),
        ),
      }))
      .filter((entry) => entry.operations.length > 0);
  }, [catalog, filter, reaches]);

  const listable = useMemo(
    () => catalog.operations.filter((op) => !REPORTING_ONLY.has(op.operation_id)).length,
    [catalog],
  );

  // How many of them this identity is expected to be refused. Counted rather than removed: it is
  // a fact about the identity, and a tester may well want to go and watch one of those refusals.
  const refused = useMemo(
    () =>
      catalog.operations.filter((op) => !REPORTING_ONLY.has(op.operation_id) && !reaches(op))
        .length,
    [catalog, reaches],
  );

  const total = groups.reduce((sum, entry) => sum + entry.operations.length, 0);

  return (
    <nav className="nav">
      <input
        className="nav__filter"
        type="search"
        value={filter}
        placeholder="Filter tasks…"
        aria-label="Filter tasks"
        onChange={(event) => setFilter(event.target.value)}
      />
      <p className="muted nav__count">
        {/* Both halves exclude the reporting views, so this never reads like a filter is on. */}
        {total} of {listable} operations
      </p>
      <p className="muted nav__count">
        {refused > 0 && (
          <>
            {refused} of them this identity should be refused &mdash; try one and see.{" "}
          </>
        )}
        Token usage is on the Overview tab.
      </p>

      {groups.map(({ group, operations }) => (
        <section key={group} className="nav__group">
          <h3>{group}</h3>
          <ul>
            {operations.map((op) => (
              <li key={op.operation_id}>
                <button
                  type="button"
                  className={op.operation_id === selected ? "nav__item nav__item--active" : "nav__item"}
                  onClick={() => onSelect(op.operation_id)}
                >
                  <span className="nav__label">{op.task_label}</span>
                  {!reaches(op) && (
                    <span
                      className="flag flag--off"
                      title="The chosen identity lacks a role this call requires — expect a 403"
                    >
                      403
                    </span>
                  )}
                  {op.danger !== "none" && (
                    <span className={`flag flag--${op.danger === "severe" ? "danger" : "warn"}`}>
                      {op.danger === "severe" ? "destructive" : "deletes"}
                    </span>
                  )}
                  {op.returns_job && <span className="flag flag--off">async</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {total === 0 && <p className="muted">Nothing matches “{filter}”.</p>}
    </nav>
  );
}
